import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import styles from './Drawer.module.scss'
import { Divider, Tag } from '@runeffective/evo-design-system'
import { Checkbox, Position } from '@blueprintjs/core'
import unseatedEmployeeCatalog from '@api/employee/unseatedEmployeeCatalog'
import { get, isEqual, unionBy } from 'lodash'
import { Avatar } from '@ui'
import unseatedCount from '@api/employee/unseatedCount'
import { Tooltip2 } from '@blueprintjs/popover2'
import { IFloorState } from '@providers/mapProvider/floorProvider/provider'

interface DraweProps {
  open: boolean
  children: ReactNode
  assignmentPlanId: string
  floorGeoLocationId: string | undefined
  floor: IFloorState
}

export const UnseatedEmployeeDrawer = ({
  open,
  assignmentPlanId,
  floorGeoLocationId,
  children,
  floor,
}: DraweProps) => {
  const [checked, setChecked] = useState(false)
  const [pageDetails, setPageDetails] = useState({})
  const [unseatedEmployees, setUnseatedEmployees] = useState([])
  const [unseatedEmpCount, setUnseatedEmpCount] = useState('')
  const [geoLocationId, setGeoLocationId] = useState(floorGeoLocationId)
  const currentLocationEmployesOnly = () => {
    setChecked(!checked)
    setPageDetails({})
    setUnseatedEmployees([])
  }

  useEffect(() => {
    setGeoLocationId(floorGeoLocationId)
    setPageDetails({})
    setUnseatedEmployees([])
  }, [setGeoLocationId, floorGeoLocationId, geoLocationId])

  const fetchUnseatedEmployeeData = useCallback(
    async (after: string | undefined) => {
      const employeesData = await unseatedEmployeeCatalog(
        assignmentPlanId,
        geoLocationId,
        after,
        checked
      )
      if (!!employeesData) {
        const employees = get(employeesData, 'viewer.company.employees.edges').map(
          ({ node }: any) => node
        )
        const uniqueEmployes = unionBy(unseatedEmployees, employees, 'id')
        if (!isEqual(uniqueEmployes, unseatedEmployees)) setUnseatedEmployees(uniqueEmployes)
        setPageDetails(get(employeesData, 'viewer.company.employees.pageInfo'))
        const unseatedEmpCount = await unseatedCount(assignmentPlanId, geoLocationId, checked)
        setUnseatedEmpCount(get(unseatedEmpCount, 'viewer.company.employees.counters.unseated'))
      }
    },
    [assignmentPlanId, geoLocationId, checked, setUnseatedEmployees, unseatedEmployees]
  )

  useEffect(() => {
    fetchUnseatedEmployeeData(undefined)
  }, [fetchUnseatedEmployeeData])

  const onScroll = (scrollEvent: any) => {
    const bottom =
      scrollEvent.target.scrollHeight - scrollEvent.target.scrollTop <=
      scrollEvent.target.clientHeight + 100
    const hasNextPage = get(pageDetails, 'hasNextPage')
    if (bottom && hasNextPage) {
      fetchUnseatedEmployeeData(get(pageDetails, 'endCursor'))
    }
  }
  return (
    <div style={{ width: open ? 320 : 34 }} className={styles.drawer}>
      <div className={styles.dividerWrapper}>
        <span className={styles.divider}>
          <Divider type="vertical"></Divider>
        </span>
      </div>
      {children}

      <div className={styles.drawerDetails}>
        <div className={styles.employeeTitleWrapper}>
          <div className={styles.employeeTitle}>Unseated employees</div>
          <Tag title={unseatedEmpCount} variant="Informational"></Tag>
        </div>
        <div className={styles.employeeCheckBoxWrapper}>
          <Tooltip2
            content={!geoLocationId ? 'Location is not set for the building' : ''}
            placement={Position.LEFT}>
            <Checkbox
              disabled={!geoLocationId}
              checked={checked}
              onChange={currentLocationEmployesOnly}>
              Current location only
            </Checkbox>
          </Tooltip2>
        </div>
        <div className={styles.employeeWrapper} onScroll={(scrollEvent) => onScroll(scrollEvent)}>
          {unseatedEmpCount ? (
            unseatedEmployees.map((emp: any) => (
              <div className={styles.employeeContainer}>
                <div style={{ marginRight: 16 }}>
                  <Avatar size={Avatar.Size.XS} name={emp?.name} assets={emp?.assets} />
                </div>
                <div>
                  <div>{emp?.name}</div>
                  <div>{emp?.dept?.name}</div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.employeeMessage}>Everyone's seated!</div>
          )}
        </div>
      </div>
    </div>
  )
}
